import Yggdrasil from 'services/yggdrasil'
import Vue from 'vue'
import i18n from 'services/i18n'
import _ from 'lodash'
import { mapOrderTypeToSortValue, orderTypes } from '../shared/sorterOptions'
import {
    initObjectWithKeysAndArrayAsDefaultValue,
    combineFiltersWithFacets,
    doSearchDefaultTimeoutDuration,
    searchDebounceDuration
} from '../shared/filtersHelper'

let currentSearchController

export const filterKeys = [
    'instructors',
    'categories',
    'authorizations',
    'course_types',
    'difficulty_levels',
    'flags',
    'language'
]

const state = {
    searchEnabled: false,
    selectedMainPackage: '',
    filters: initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
    facets: initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
    sort: orderTypes.A_TO_Z,
    viewType: 'all',
    viewCount: {
        courses: 0,
        collections: 0,
        all: 0,
        live: 0,
        virtual_classrooms: 0,
        learning_paths: 0
    },
    currentOffset: 0,
    filteredCount: 0,
    outsidePackageScopeCount: 0,
    searchQuery: '',
    searchComponentKey: Date.now(),
    hideCompletedCourses: false,
    isDemoContentHidden: false,
    foundItems: [],
    filtersWithFacets: initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
    filterStaticData: {
        instructors: {
            filterKey: 'instructors',
            translationString: 'CourseListFilter.instructor'
        },
        categories: {
            filterKey: 'categories',
            translationString: 'CourseListFilter.categories'
        },
        flags: {
            filterKey: 'flags',
            translationString: 'CourseListFilter.show',
            labelsDomains: {
                new: 'Ribbon.new',
                coming: 'Ribbon.coming',
                expiring: 'Ribbon.expiring',
                highlight: 'Ribbon.favourite',
                trending: 'Ribbon.topical',
                customer: 'Ribbon.customer',
                free: 'General.free',
                promotional: 'General.promotional',
                upcoming: 'General.upcoming'
            }
        },
        difficulty_levels: {
            filterKey: 'difficulty_levels',
            translationString: 'CourseListFilter.skill_level',
            labelsDomains: {
                0: 'Select.skill_level_0',
                1: 'Select.skill_level_1',
                2: 'Select.skill_level_2',
                3: 'Select.skill_level_3',
                4: 'Select.skill_level_4'
            }
        },
        course_types: {
            filterKey: 'course_types',
            translationString: 'CourseListFilter.course_type',
            labelsDomains: {
                live: 'CourseListFilter.live',
                recording: 'CourseListFilter.recording',
                virtual_classroom: 'CourseListFilter.virtual_classroom',
                podcast: 'CourseListFilter.podcast'
            }
        },
        authorizations: {
            filterKey: 'authorizations',
            translationString: 'CourseListFilter.authorization'
        },
        language: {
            filterKey: 'language',
            translationString: 'CourseListFilter.language',
            labelsDomains: {
                fi: 'CourseListFilter.language_fi',
                se: 'CourseListFilter.language_se',
                no: 'CourseListFilter.language_no',
                en: 'CourseListFilter.language_en',
                de: 'CourseListFilter.language_de',
                es: 'CourseListFilter.language_es',
                zh: 'CourseListFilter.language_zh',
                it: 'CourseListFilter.language_it',
                fr: 'CourseListFilter.language_fr',
                ru: 'CourseListFilter.language_ru'
            }
        }
    },
    categoryTree: [],
    searchInProgress: false
}

export const viewTypes = {
    all: 'all',
    collections: 'collections',
    courses: 'courses',
    liveEvents: 'live',
    virtualClassrooms: 'virtual_classrooms',
    learningPaths: 'learning_paths'
}

let doSearchTimeout = null

const mutations = {
    SET_FLAG_OPTIONS (state, flagOptions) {
        state.flagOptions = flagOptions
    },
    SET_DIFFICULTY_LEVEL_OPTIONS (state, difficultyLevelOptions) {
        state.difficultyLevelOptions = difficultyLevelOptions
    },
    SET_MAIN_PACKAGE (state, mainPackage) {
        state.selectedMainPackage = mainPackage
    },
    SET_SEARCH_RESULTS (state, results) {
        state.foundItems = state.foundItems.concat(results.result)
        state.currentOffset = state.foundItems.length
        state.filteredCount = results.filtered_count
        state.viewCount = results.view_type_count
        state.outsidePackageScopeCount = results.outside_package_scope_count
        Object.keys(state.facets).forEach(facetKey => {
            state.facets[facetKey] = results.facets[facetKey] ?? []
        })
        state.filtersWithFacets = combineFiltersWithFacets(state.filters, state.facets, filterKeys)
    },
    SET_QUERY (state, query) {
        state.currentOffset = 0
        state.searchQuery = query
    },
    SET_ITEM_TYPE (state, viewType) {
        state.viewType = viewType
    },
    SET_SORT (state, sort) {
        state.sort = sort
    },
    SET_COMPLETED_COURSES_TOGGLE (state, value) {
        state.hideCompletedCourses = value
    },
    SET_VISIBILITY_OF_DEMO_CONTENT (state, value) {
        state.isDemoContentHidden = value
    },
    ADD_FILTER_VALUE (state, valueOptions) {
        state.filters[valueOptions.key].push({ value: valueOptions.value, label: valueOptions.label })
    },
    REMOVE_FILTER_VALUE (state, valueOptions) {
        state.filters[valueOptions.key] = state.filters[valueOptions.key].filter(filterValue => filterValue.value !== valueOptions.value)
    },
    SET_FILTER_VALUES (state, valueOptions) {
        let filters = valueOptions.filters.map(option => {
            return {
                value: option.id,
                label: option.labelWithoutCount
            }
        })
        Vue.set(state.filters, valueOptions.key, filters)
    },
    CLEAR_ALL_FILTERS (state) {
        state.filters = initObjectWithKeysAndArrayAsDefaultValue(filterKeys)
        state.hideCompletedCourses = false
    },
    CLEAR_FILTER (state, key) {
        if (state.filters[key]) {
            Vue.set(state.filters, key, [])
        }
    },
    CLEAR_ITEMS (state) {
        state.foundItems = []
    },
    RESET_PAGINATION (state) {
        state.currentOffset = 0
    },
    SET_OFFSET (state, offset) {
        state.currentOffset = offset
    },
    ASSIGN_CATEGORY_TREE (state, categoryTree) {
        state.categoryTree = categoryTree
    },
    SET_SEARCH_ENABLED (state, searchEnabled) {
        state.searchEnabled = searchEnabled
    },
    SET_SEARCH_IN_PROGRESS (state, searchInProgress) {
        state.searchInProgress = searchInProgress
    },
    SET_DEFAULT_SORT_VALUE (state, sort) {
        state.sort = sort
    },
    SET_SEARCH_COMPONENT_KEY (state, key) {
        state.searchComponentKey = key
    }
}

function compiledFilter (state) {
    const compiledFilter = {
        filter: {
            packages: [state.selectedMainPackage],
            hideCompletedCourses: state.hideCompletedCourses,
            hideDemoContent: state.isDemoContentHidden
        }
    }
    Object.keys(state.filters).forEach(filterKey => {
        if (!state.filters[filterKey].length) {
            return
        }
        compiledFilter.filter[filterKey] = state.filters[filterKey].map(el => el.value)
    })
    if (state.searchQuery) {
        compiledFilter.query = state.searchQuery
    }
    if (state.viewType !== viewTypes.collections && state.viewType !== null) {
        compiledFilter.viewType = state.viewType
    }
    compiledFilter.sort = {
        type: state.sort.type,
        order: state.sort.order
    }
    compiledFilter.offset = state.currentOffset
    return compiledFilter
}

function filterTreeByCategories (tree, categories) {
    return tree.filter(leaf => categories.find(cat => +cat.value === +leaf.id)).map(leaf => {
        if (leaf.children) {
            return {
                label: getFacetsAttributes(leaf, categories).label,
                labelWithoutCount: getFacetsAttributes(leaf, categories).labelWithoutCount,
                id: getFacetsAttributes(leaf, categories).value,
                selected: getFacetsAttributes(leaf, categories).selected,
                children: filterTreeByCategories(leaf.children, categories)
            }
        } else {
            return {
                label: getFacetsAttributes(leaf, categories).label,
                labelWithoutCount: getFacetsAttributes(leaf, categories).labelWithoutCount,
                id: getFacetsAttributes(leaf, categories).value,
                selected: getFacetsAttributes(leaf, categories).selected,
            }
        }
    }).sort((itemA, itemB) => itemA.label.localeCompare(itemB.label))
}

function getFacetsAttributes (leaf, categories) {
    return categories.find(cat => +cat.value === +leaf.id)
}

const actions = {
    setMainPackage (store, mainPackage) {
        if (store.state.selectedMainPackage === mainPackage) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_MAIN_PACKAGE', mainPackage)
        store.dispatch('doSearchNow')
    },
    setQuery (store, query) {
        if (store.state.searchQuery === query) {
            return
        }
        if (query === null && store.state.viewType === viewTypes.all) {
            store.commit('SET_SORT', mapOrderTypeToSortValue(orderTypes.A_TO_Z))
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_QUERY', query)
        store.dispatch('doSearch')
    },
    setViewType (store, viewType) {
        if (store.state.viewType === viewType) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_ITEM_TYPE', viewType)
        store.dispatch('doSearchNow')
    },
    setSort (store, sort) {
        if (_.isEqual(store.state.sort, sort)) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_SORT', sort)
        store.dispatch('doSearchNow')
    },
    setCompletedCoursesToggle (store, value) {
        if (store.state.hideCompletedCourses === value) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_COMPLETED_COURSES_TOGGLE', value)
        store.dispatch('doSearchNow')
    },
    setHideDemoContentToggle (store, value) {
        if (store.state.isDemoContentHidden === value) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_VISIBILITY_OF_DEMO_CONTENT', value)
        store.dispatch('doSearchNow')
    },
    addFilterValue (store, valueOptions) {
        store.commit('RESET_PAGINATION')
        store.commit('ADD_FILTER_VALUE', valueOptions)
        store.dispatch('doSearchNow')
    },
    removeFilterValue (store, valueOptions) {
        store.commit('RESET_PAGINATION')
        store.commit('REMOVE_FILTER_VALUE', valueOptions)
        store.dispatch('doSearchNow')
    },
    setFilterValues (store, payload) {
        store.commit('RESET_PAGINATION')
        store.commit('SET_FILTER_VALUES', payload)
        store.dispatch('doSearchNow')
    },
    clearAllFilters (store) {
        store.commit('RESET_PAGINATION')
        store.commit('CLEAR_ALL_FILTERS')
        store.dispatch('doSearchNow')
    },
    clearFilter (store, { key }) {
        store.commit('RESET_PAGINATION')
        store.commit('CLEAR_FILTER', key)
        store.dispatch('doSearchNow')
    },
    clearItemsAndResetPagination (store) {
        store.commit('CLEAR_ITEMS')
        store.commit('RESET_PAGINATION')
    },
    async doSearch (store, options = { timeout: doSearchDefaultTimeoutDuration }) {
        clearTimeout(doSearchTimeout)
        doSearchTimeout = setTimeout(() => store.dispatch('doSearchNow'), options.timeout)
    },
    async doSearchNow (store) {
        store.commit('SET_SEARCH_IN_PROGRESS', true)
        if (currentSearchController) {
            currentSearchController.abort()
        }
        clearTimeout(doSearchTimeout)
        if (store.state.searchEnabled !== true) {
            return
        }
        doSearchTimeout = setTimeout(async () => {
            try {
                currentSearchController = new AbortController()
                await store.dispatch('LoaderScreen/addTaskToLoader', {
                    key: 'search',
                    message: 'Loading data'
                }, { root: true })
                const searchResult = await Yggdrasil.search(compiledFilter(store.state), currentSearchController.signal)
                if (store.state.currentOffset === 0) {
                    store.commit('CLEAR_ITEMS')
                }
                store.commit('SET_SEARCH_RESULTS', searchResult)
                store.commit('SET_SEARCH_IN_PROGRESS', false)
            } catch (e) {
            }
            store.commit('SET_SEARCH_IN_PROGRESS', false)
            store.dispatch('LoaderScreen/removeTaskFromLoader', 'search', { root: true })
        }, searchDebounceDuration)
    },
    async loadCategories (store) {
        const categoryTree = await Yggdrasil.loadCategories()
        store.commit('ASSIGN_CATEGORY_TREE', categoryTree)
    },
    enableSearch (store) {
        store.commit('SET_SEARCH_ENABLED', true)
    },
    disableSearch (store) {
        store.commit('SET_SEARCH_ENABLED', false)
    },
    setDefaultSortValue (store) {
        let sort
        switch (state.viewType) {
        case viewTypes.learningPaths:
        case viewTypes.collections:
        case viewTypes.courses:
            sort = orderTypes.NEW_TO_OLD
            break
        case viewTypes.liveEvents:
        case viewTypes.virtualClassrooms:
            sort = orderTypes.OLD_TO_NEW
            break
        case viewTypes.all:
            sort = orderTypes.A_TO_Z
        }
        store.commit('SET_DEFAULT_SORT_VALUE', mapOrderTypeToSortValue(sort))
    },
    setSearchComponentKey (store) {
        store.commit('SET_SEARCH_COMPONENT_KEY', Date.now())
    }
}

const getters = {
    searchQuery: state => state.searchQuery,
    getSearchComponentKey: state => state.searchComponentKey,
    facets: state => state.facets,
    mainPackage: state => state.selectedMainPackage,
    selectedMainPackage: (state) => state.selectedMainPackage,
    filters: state => state.filters,
    shouldHintButtonToSwitchCategoryBeenDisplayed: (state) => {
        const outsidePackageScopeCount = state.outsidePackageScopeCount > 0
        if (!outsidePackageScopeCount || !state.selectedMainPackage) {
            return false
        }
        const isAnyFilterSelected = Object.keys(state.filters).map(key => state.filters[key]?.length > 0).filter(el => el).length > 0
        return state.searchQuery || isAnyFilterSelected
    },
    filterWithKey: (state) => (key) => state.filters[key],
    filteredCount: state => state.filteredCount,
    searchResults: state => state.foundItems,
    viewTypeCounts: state => state.viewCount,
    hasMoreResults: state => {
        return state.foundItems.length < state.filteredCount
    },
    viewTypes: state => viewTypes,
    filtersWithFacets: (state) => state.filtersWithFacets,
    tags (store, getters) {
        return Object.keys(getters.filtersWithFacets).reduce((tags, filterKey) => {
            return tags.concat(
                getters.filtersWithFacets[filterKey]
                    .filter(filterItem => filterItem.selected)
                    .map(filterItem => ({
                        value: filterItem.value,
                        label: filterItem.label,
                        count: filterItem.count,
                        filterKey: filterKey,
                    })
                    ))
        }, [])
    },
    getSelectedMainPackage: (state) => state.selectedMainPackage,
    getOutsidePackageScopeCount: (state) => state.outsidePackageScopeCount,
    getCurrentSort: (state) => state.sort,
    getFilterStaticData: (state) => {
        const flagLabels = {}
        for (const [key, translationDomain] of Object.entries(state.filterStaticData.flags.labelsDomains)) {
            flagLabels[key] = i18n.t(translationDomain)
        }
        const languageLabels = {}
        for (const [key, translationDomain] of Object.entries(state.filterStaticData.language.labelsDomains)) {
            languageLabels[key] = i18n.t(translationDomain)
        }
        const difficultyLevelsLabels = {}
        for (const [key, translationDomain] of Object.entries(state.filterStaticData.difficulty_levels.labelsDomains)) {
            difficultyLevelsLabels[key] = i18n.t(translationDomain)
        }
        const courseTypesLabels = {}
        for (const [key, translationDomain] of Object.entries(state.filterStaticData.course_types.labelsDomains)) {
            courseTypesLabels[key] = i18n.t(translationDomain)
        }
        return {
            ...state.filterStaticData,
            flags: {
                ...state.filterStaticData.flags,
                labels: flagLabels
            },
            language: {
                ...state.filterStaticData.language,
                labels: languageLabels
            },
            difficulty_levels: {
                ...state.filterStaticData.difficulty_levels,
                labels: difficultyLevelsLabels
            },
            course_types: {
                ...state.filterStaticData.course_types,
                labels: courseTypesLabels
            }
        }
    },
    getFilteredCategories: (state, getters) => (filterBy) => {
        let categories = getters.getCategoryTree.slice()
        let filterByCategories = filterBy.slice()
        return filterTreeByCategories(categories, filterByCategories)
    },
    getCategoryTree: (state) => {
        if (state.categoryTree.length === 1) {
            return state.categoryTree[0].children // We force category as a root if there is only one of them
        }
        return state.categoryTree
    },
    getViewType: (state) => state.viewType,
    isThereAnyActiveFilter: (state) => {
        for (const filter in state.filters) {
            if (state.filters[filter].length > 0) {
                return true
            }
        }
        return false
    },
    getHideCompletedCourses: (state) => state.hideCompletedCourses,
    isDemoContentHidden: (state) => state.isDemoContentHidden,
    isSearchInProgress: (state) => state.searchInProgress
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
